import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppGlobalsModule, AppGlobalsModule as _globals } from '../app-globals.module';
import { filter, first, map, share, shareReplay, switchMap, take } from 'rxjs/operators';
import { AngularApp } from '../../../src/serverTypes/yeshivaTypesDeclare';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { AngularPages, SiteLocations, UrlAddressService } from '../core/services/url-address/url-address.service';
import { Router } from '@angular/router';
import { UtilService } from '../core/services/util/util.service';
import { isErrorPageUrl } from '../core/errors/error-page-routes';

@Injectable()
export class SiteHeaderService implements OnDestroy {
    private http = inject(HttpClient);
    private _urlAddress = inject(UrlAddressService);
    private _router = inject(Router);

    private siteHeader$: Observable<AngularApp.API.SiteHeader> | undefined;

    constructor() {
        this._isMainMenuOpen$ = new BehaviorSubject<boolean>(false);
    }

    ngOnDestroy(): void {
        if (this._isMainMenuOpen$) {
            this._isMainMenuOpen$.unsubscribe();
        }
        if (this._headerMode$) {
            this._headerMode$.unsubscribe();
        }
        if (this._heightHeaderSticky$) {
            this._heightHeaderSticky$.unsubscribe();
        }
    }

    private siteHeaderDataUrl = _globals.apiPath('siteheader');

    public getHeaderData() {
        if (!this.siteHeader$) {
            this.siteHeader$ = this.http.get<AngularApp.API.SiteHeader>(this.siteHeaderDataUrl).pipe(shareReplay({ bufferSize: 1, refCount: true }), first());
        }
        return this.siteHeader$;
    }

    private _headerMode$ = new Subject<HeaderMode>();
    public getHeaderMode$(): Observable<HeaderMode> {
        return this._headerMode$.asObservable().pipe(filter((headerMode) => headerMode !== undefined));
    }
    public setHeaderMode$(headerMode: HeaderMode): void {
        this._headerMode$.next(headerMode);
    }

    public getHeaderMode(activeLocation: SiteLocations = this._urlAddress.getActiveLocation(), activeAngularPage?: AngularPages, isAtTop = true): HeaderMode {
        let headerMode: HeaderMode;
        if (this._urlAddress.isDedicationDatePicker) {
            headerMode = HeaderMode.Iframe;
        } else if (/^\/nihul($|[/?#])/.test(this._urlAddress.getPathname())) {
            //the nihul always shows the compact scrolled header - a work area keeps the site
            //chrome minimal. This is THE mode source, so the esi include (mode= param) and the
            //ssr fragment come out minimal too, and scroll position stops mattering there
            headerMode = HeaderMode.Scroll;
        } else {
            if (isAtTop) {
                if (isErrorPageUrl(this._router.url)) {
                    // In error pages we want the inner header with no transparency, because the background is white.
                    headerMode = HeaderMode.Inner;
                } else if (!activeAngularPage) {
                    if (activeLocation === SiteLocations.Ask) {
                        // HOTFIX: ask/ask is not angular but inner page, getting caught in "contentAreaRoutes"
                        headerMode = HeaderMode.Inner;
                    } else if (
                        activeLocation === SiteLocations.Main ||
                        this._urlAddress.contentAreaRoutes.includes(this._urlAddress.rootAngularUrl) ||
                        this._router.url.includes(AngularPages.Category) ||
                        this._router.url.includes(AngularPages.Article) ||
                        this._router.url.includes(AngularPages.Search) ||
                        this._router.url.includes(AngularPages.Contact) ||
                        this._router.url.includes(AngularPages.ShabbatTimes) ||
                        this._router.url.includes(AngularPages.TimesTable) ||
                        this._router.url.includes(AngularPages.Moladot) ||
                        this._router.url.includes(AngularPages.Series)
                    ) {
                        headerMode = HeaderMode.Transparent;
                    } else {
                        headerMode = HeaderMode.Inner;
                    }
                } else {
                    if (
                        activeLocation === SiteLocations.Main ||
                        activeAngularPage.startsWith(AngularPages.Exams) ||
                        [
                            AngularPages.Home,
                            AngularPages.Category,
                            AngularPages.Article,
                            AngularPages.Search,
                            AngularPages.ShabbatTimes,
                            AngularPages.TimesTable,
                            AngularPages.Moladot,
                            AngularPages.Contact,
                            AngularPages.Series,
                        ].includes(activeAngularPage)
                    ) {
                        headerMode = HeaderMode.Transparent;
                    } else {
                        headerMode = HeaderMode.Inner;
                    }
                }
            } else {
                headerMode = HeaderMode.Scroll;
            }
        }
        return headerMode;
    }

    private _heightHeaderSticky$ = new Subject<number>();
    public getHeightHeaderSticky(): Observable<number> {
        return this._heightHeaderSticky$.asObservable();
    }
    public setHeightHeaderSticky(_heightHeaderSticky: number): void {
        this._heightHeaderSticky$.next(_heightHeaderSticky);
    }

    // ===========================
    // Belongs to both header and footer The appropriate place is in the wrapper that belongs to both
    private _isMainMenuOpen$: BehaviorSubject<boolean>;
    public getIsMainMenuOpen(): Observable<boolean> {
        return this._isMainMenuOpen$.asObservable();
    }
    public setIsMainMenuOpen(isMainMenuOpen: boolean): void {
        this._isMainMenuOpen$.next(isMainMenuOpen);
    }

    public getLogoSvg() {
        return this.getHeaderData().pipe(
            take(1),
            switchMap((data) => {
                // We need to bypass and create the SVG manually otherwise it wouldn't be possible to change it's color and size.
                if (data.SiteLogo) {
                    let logoPath = AppGlobalsModule.apiPath(data.SiteLogo, '', '');
                    return this.http.get(logoPath, { responseType: 'text' });
                } else {
                    // There is no logo so we return nothing (EMPTY) that complete the observable
                    return EMPTY;
                }
            }),
        );
    }
    //============================

    public getEmptyDailyDonor() {
        return $localize`:@@empty-daily-donor:הקדשת הלימוד באתר`;
    }
}

export interface BannerTop {
    txt: string;
    link: string;
    clr: string;
    bgClr: string;
}

export enum HeaderMode {
    Inner = 'inner-mode', // In inner and non angular pages
    Transparent = 'transparent-mode', // This is on the top of the main pages
    Scroll = 'scroll-mode', // When scrolling down
    Iframe = 'iframe',
}
